export type PredictionGraphicMode = 'auto' | 'election' | 'versus_promo' | 'versus_matchup'

export type PredictionDisplayConfig = {
  graphicMode?: PredictionGraphicMode
  matchupFocus?: string
  backgroundImageUrl?: string | null
  promoBackgroundImageUrl?: string | null
  matchupBackgroundImageUrl?: string | null
  backgroundOverlayOpacity?: number
}

export type PredictionPrizeInfo = {
  headline?: string
  description?: string
  rewardTypes?: Array<'ODDS' | 'POINTS_BONUS' | 'PHYSICAL' | 'CUSTOM'>
  extraNote?: string
}

export const DEFAULT_PREDICTION_DISPLAY_CONFIG: PredictionDisplayConfig = {
  graphicMode: 'auto',
  matchupFocus: 'main',
  backgroundImageUrl: null,
  promoBackgroundImageUrl: null,
  matchupBackgroundImageUrl: null,
  backgroundOverlayOpacity: 0.6,
}

export const DEFAULT_PRIZE_INFO: PredictionPrizeInfo = {
  headline: '',
  description: '',
  rewardTypes: [],
  extraNote: '',
}

export const GRAPHIC_MODE_OPTIONS: Array<{ value: PredictionGraphicMode; label: string; desc: string }> = [
  { value: 'auto', label: 'อัตโนมัติ', desc: 'เลือกตั้ง → Top 5 · อื่นๆ → VS' },
  { value: 'versus_promo', label: 'แบบ A — โปรโมตการ์ด', desc: 'Main Event + Undercard (เชิญชวนเล่น)' },
  { value: 'versus_matchup', label: 'แบบ B — โฟกัส 1 คู่', desc: 'แสดงคู่เดียว (ดูผล / TV รายคู่)' },
  { value: 'election', label: 'กราฟเลือกตั้ง', desc: 'Top 5 อันดับ' },
]

export type PrizeRewardType = 'ODDS' | 'POINTS_BONUS' | 'PHYSICAL' | 'CUSTOM'

export const PRIZE_TYPE_OPTIONS: Array<{ value: PrizeRewardType; label: string }> = [
  { value: 'ODDS', label: 'จ่ายตาม Odds (เงิน/พอยท์อัตโนมัติ)' },
  { value: 'POINTS_BONUS', label: 'โบนัสพอยท์ / แต้มพิเศษ' },
  { value: 'PHYSICAL', label: 'ของรางวัล / รางวัลจริง' },
  { value: 'CUSTOM', label: 'อื่นๆ (ระบุในคำอธิบาย)' },
]

export function normalizePredictionDisplayConfig(
  input?: PredictionDisplayConfig | null,
): PredictionDisplayConfig {
  if (!input) return { ...DEFAULT_PREDICTION_DISPLAY_CONFIG }
  const opacity = Number(input.backgroundOverlayOpacity)
  const legacy = input.backgroundImageUrl?.trim() || null
  return {
    graphicMode: input.graphicMode || 'auto',
    matchupFocus: input.matchupFocus || 'main',
    backgroundImageUrl: legacy,
    promoBackgroundImageUrl: input.promoBackgroundImageUrl?.trim() || legacy,
    matchupBackgroundImageUrl: input.matchupBackgroundImageUrl?.trim() || legacy,
    backgroundOverlayOpacity: Number.isFinite(opacity)
      ? Math.min(1, Math.max(0, opacity))
      : DEFAULT_PREDICTION_DISPLAY_CONFIG.backgroundOverlayOpacity,
  }
}

export function normalizePrizeInfo(input?: PredictionPrizeInfo | null): PredictionPrizeInfo {
  if (!input) return { ...DEFAULT_PRIZE_INFO }
  const allowed = new Set(['ODDS', 'POINTS_BONUS', 'PHYSICAL', 'CUSTOM'])
  return {
    headline: input.headline?.trim() || '',
    description: input.description?.trim() || '',
    rewardTypes: (input.rewardTypes || []).filter((t) => allowed.has(t)),
    extraNote: input.extraNote?.trim() || '',
  }
}

export function resolveGraphicMode(
  config: PredictionDisplayConfig | null | undefined,
  category: string,
): PredictionGraphicMode {
  const mode = config?.graphicMode || 'auto'
  if (mode !== 'auto') return mode
  return String(category || '').toUpperCase() === 'ELECTION' ? 'election' : 'versus_promo'
}

export function resolveBackgroundForGraphicMode(
  config: PredictionDisplayConfig | null | undefined,
  graphicMode: PredictionGraphicMode,
): Pick<PredictionDisplayConfig, 'backgroundImageUrl' | 'backgroundOverlayOpacity'> {
  const normalized = normalizePredictionDisplayConfig(config)
  if (graphicMode === 'versus_matchup') {
    return {
      backgroundImageUrl: normalized.matchupBackgroundImageUrl || normalized.backgroundImageUrl || null,
      backgroundOverlayOpacity: normalized.backgroundOverlayOpacity,
    }
  }
  if (graphicMode === 'versus_promo') {
    return {
      backgroundImageUrl: normalized.promoBackgroundImageUrl || normalized.backgroundImageUrl || null,
      backgroundOverlayOpacity: normalized.backgroundOverlayOpacity,
    }
  }
  return {
    backgroundImageUrl: normalized.backgroundImageUrl || normalized.promoBackgroundImageUrl || null,
    backgroundOverlayOpacity: normalized.backgroundOverlayOpacity,
  }
}

export function overlayOpacityPercent(config?: PredictionDisplayConfig | null): number {
  const normalized = normalizePredictionDisplayConfig(config)
  return Math.round((normalized.backgroundOverlayOpacity ?? 0.6) * 100)
}

export function overlayOpacityFromPercent(percent: number): number {
  return Math.min(1, Math.max(0, percent / 100))
}

export function listMatchupFocusOptions(pairCount: number): Array<{ value: string; label: string }> {
  const options: Array<{ value: string; label: string }> = [
    { value: 'main', label: 'คู่เอก (Main Event) — คู่ที่ 1' },
  ]
  for (let i = 0; i < Math.max(0, pairCount - 1); i += 1) {
    options.push({ value: `undercard:${i}`, label: `Undercard คู่ที่ ${i + 1}` })
  }
  return options
}

export function countVersusPairs(selectionCount: number): number {
  return Math.max(0, Math.ceil(selectionCount / 2))
}

export function toDatetimeLocalValue(iso?: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function fromDatetimeLocalValue(value: string): string | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const d = new Date(trimmed)
  if (Number.isNaN(d.getTime())) return null
  return d.toISOString()
}
